<script setup lang="ts">
/**
 * QUICK RECOVER — helper plates: low nerve → "Stiff Drink" (full nerve, offered
 * below 25% nerve), any missing HP → "Back-Alley Patch-Up" (full heal, offered
 * the moment HP is off 100% — not just near-death; the pulsing "hurry" glow is
 * reserved for the urgent case, below 35% HP, so a barely-scratched top-off
 * still reads as a quiet helper plate, not an alarm). One tap buys straight
 * through the shop pipeline (same items and prices as The Quarter's Supplies
 * tab; no daily cap — buy as often as you can afford). Used on the City Map
 * HUD and the Hero page.
 */
import { ref, computed, onMounted } from 'vue';
import { fetchShop, buyShopItem, type ShopItem } from '../game/shop-api';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import nerveIcon from '../assets/omerta/icons/nerve.webp';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import { SHOP_ART } from '../game/art';

const props = defineProps<{
  hp?: number; maxHp?: number;
  /** BASE max HP — the ceiling fights actually run at. Gear (+HP) inflates
      maxHp for the out-of-combat pool, but combat is capped at base, so
      "do I need a patch-up?" is judged against this, not maxHp. */
  fullHp?: number;
  nerve?: number; maxNerve?: number;
  cash?: number;
  goldBars?: number;
  /** Regen anchors — when present, the plates say how long the free road takes. */
  hpRegen?: { exact: number; perMin: number };
  nerveRegen?: { exact: number; perMin: number };
}>();
const emit = defineEmits<{ (e: 'updated'): void }>();

const URGENT_HP = 0.35; // pulsing "hurry" glow kicks in below 35% health
const LOW_NERVE = 0.25; // stiff-drink offer below 25% nerve

// One catalog fetch per session — every instance shares it.
let catalogOnce: Promise<ShopItem[]> | null = null;
const items = ref<ShopItem[]>([]);
onMounted(async () => {
  catalogOnce ??= fetchShop().then((v) => v.catalog);
  try { items.value = await catalogOnce; } catch { /* buttons simply stay hidden */ }
});

const drink = computed(() => items.value.find((i) => i.kind === 'nerve_refill'));
const patch = computed(() => items.value.find((i) => i.kind === 'hp_refill'));

const lowNerve = computed(() =>
  props.nerve != null && props.maxNerve ? props.nerve / props.maxNerve <= LOW_NERVE : false);
// Patch-up shows on ANY missing FIGHT health (QA: not just a near-death save) —
// but judged against the BASE ceiling fights actually run at. Gear (+HP) inflates
// the display pool (e.g. 502/612 with base 460), and every fight persists HP at
// ≤ base, so against maxHp the button read "you're hurt" at what is combat-full
// health and effectively never disappeared. The pulsing glow stays urgent-only.
const hpCeiling = computed(() => {
  const cap = props.fullHp ?? props.maxHp;
  return cap != null && props.maxHp != null ? Math.min(cap, props.maxHp) : cap;
});
const lowHp = computed(() =>
  props.hp != null && hpCeiling.value ? props.hp < hpCeiling.value : false);
const urgentHp = computed(() =>
  props.hp != null && hpCeiling.value ? props.hp / hpCeiling.value <= URGENT_HP : false);

const busy = ref<string | null>(null);
const note = ref('');

async function buy(item: ShopItem) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: 'Quick recovery',
    title: `Buy ${item.name}?`,
    icon: item.kind === 'nerve_refill' ? nerveIcon : SHOP_ART.hp_refill,
    rows: [
      { label: 'Price', value: item.price.toLocaleString(), icon: cashIcon },
    ],
    note: item.description ?? undefined,
    confirmLabel: 'Buy it',
  });
  if (!ok) return;
  busy.value = item.id; note.value = '';
  try {
    await buyShopItem(item.id);
    toast(item.kind === 'nerve_refill' ? 'Nerve restored — back on the street' : 'Patched up to full health',
      { icon: item.kind === 'nerve_refill' ? nerveIcon : SHOP_ART.hp_refill });
    emit('updated');
  } catch (e) {
    note.value = (e as Error).message; // daily cap hit, short on cash, …
    window.setTimeout(() => { note.value = ''; }, 4000);
  } finally {
    busy.value = null;
  }
}

const affordable = (item?: ShopItem) => {
  if (item == null) return false;
  const bal = item.currency === 'PREMIUM' ? props.goldBars : props.cash;
  return bal == null || bal >= item.price;
};

// The honest alternative to paying: how long the free regen takes. HP counts
// to the FIGHT ceiling (fullHp), matching the same rule the plates show up by.
const hpClock = useRegenClock(() =>
  props.hpRegen && hpCeiling.value ? { anchor: props.hpRegen, max: hpCeiling.value } : null);
const nerveClock = useRegenClock(() =>
  props.nerveRegen && props.maxNerve ? { anchor: props.nerveRegen, max: props.maxNerve } : null);
</script>

<template>
  <div v-if="(lowNerve && drink) || (lowHp && patch)" class="qr">
    <button
      v-if="lowNerve && drink"
      class="btn-deco btn-deco--sm qr-btn qr-btn-pulse"
      :disabled="busy !== null || !affordable(drink)"
      :title="drink.description"
      @click="buy(drink)"
    >
      <img :src="nerveIcon" alt="" class="w-3.5 h-3.5 shrink-0" />
      <span>{{ busy === drink.id ? '…' : drink.name }}</span>
      <span class="qr-price"><img :src="drink.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" alt="" class="w-3 h-3 shrink-0" />{{ drink.price }}</span>
      <span v-if="nerveClock && !nerveClock.full" class="qr-eta tabular-nums">or free in {{ fmtEta(nerveClock.fullIn) }}</span>
    </button>

    <button
      v-if="lowHp && patch"
      :class="['btn-deco btn-deco--sm qr-btn qr-btn-hp', { 'qr-btn-pulse': urgentHp }]"
      :disabled="busy !== null || !affordable(patch)"
      :title="patch.description"
      @click="buy(patch)"
    >
      <img :src="SHOP_ART.hp_refill" alt="" class="w-3.5 h-3.5 shrink-0 object-contain" />
      <span>{{ busy === patch.id ? '…' : patch.name }}</span>
      <span class="qr-price"><img :src="patch.currency === 'PREMIUM' ? goldBarsIcon : cashIcon" alt="" class="w-3 h-3 shrink-0" />{{ patch.price }}</span>
      <span v-if="hpClock && !hpClock.full" class="qr-eta tabular-nums">or free in {{ fmtEta(hpClock.fullIn) }}</span>
    </button>

    <span v-if="note" class="qr-note">{{ note }}</span>
  </div>
</template>

<style scoped>
.qr {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  pointer-events: auto;
}
/* pulse is opt-in per instance (.qr-btn-pulse) — the HP plate only earns it
   below the urgent threshold; a barely-scratched top-off stays a quiet plate. */
.qr-btn-pulse { animation: qr-pulse 2.2s ease-in-out infinite; }
.qr-btn:disabled { animation: none; }
.qr-btn-hp { --bd-rim: linear-gradient(180deg, #7e3040, #3d1219 55%, #64242f); --bd-ink: #dfa898; --bd-ink-hover: #ffd9cc; }
@keyframes qr-pulse {
  0%, 100% { filter: drop-shadow(0 0 3px rgba(200, 162, 76, 0.15)); }
  50% { filter: drop-shadow(0 0 10px rgba(200, 162, 76, 0.4)); }
}
.qr-price {
  display: inline-flex; align-items: center; gap: 3px;
  color: var(--color-gold-bright);
}
/* the free road: quiet, non-competing with the price */
.qr-eta { font-size: 10px; color: var(--color-text-dim); letter-spacing: 0.02em; }
.qr-note {
  font-family: var(--font-body, serif);
  font-size: 12px; font-style: italic;
  color: var(--color-error);
}
@media (prefers-reduced-motion: reduce) { .qr-btn { animation: none; } }
</style>
